import React, { useCallback, useEffect, useRef } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { BLOCKED_DATES_LIST } from 'consts';
import { useStore } from 'effector-react';
import { useHideChats } from 'hooks';
import { changeFocusedPickerDay, picker_state$ } from 'models/cart';
import { currProduct$ } from 'models/product';
import DatePickerDisclosureItem from 'pages/Product/MainBlock/DatesPanel/DatePickerDisclosureItem';
import { useCalendarSettings } from 'pages/Product/useCalendarSettings';
import { formatDate, getFormattedHireDates } from 'utils/datesСalculation';

import { Button, DatePickerCalendar, T8y } from 'ui';

import { ReactComponent as ClearIcon } from 'assets/images/icons/closeFilterSearch.svg';

import style from './calendarModal.module.scss';

const MobileCalendarModal = ({
  selectingDetails,
  setSelectingDetails,
  setIsCalendarModalOpen,
  daysText,
  isDatesHighlighted,
  suggestions = [],
}) => {
  const calendarWrapperRef = useRef(null);

  const state = useStore(picker_state$);
  const product = useStore(currProduct$);

  useHideChats({ isAlwaysHide: true });

  const {
    selectedValues,
    onDatesChanges,
    hiringDaysCount,
    minBookingDays,
    minBookingDate,
    maxBookingDate,
  } = useCalendarSettings(product, selectingDetails.dates);

  const { startDate, endDate } = getFormattedHireDates(
    'en-gb',
    selectedValues.startDate,
    selectedValues.endDate,
  );

  // DO NOT disable scroll on touch devices
  // https://github.com/reakit/reakit/issues/469
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const scrollBox = calendarWrapperRef.current;

    disableBodyScroll(scrollBox, {
      allowTouchMove: (el) => {
        return !!el.closest('#suggested-dates');
      },
    });

    return () => {
      enableBodyScroll(scrollBox);
      document.body.style.removeProperty('overflow');
    };
  }, []);

  const handleChangeDates = useCallback(() => {
    setSelectingDetails((currDetails) => ({
      ...currDetails,
      dates: {
        start: formatDate(selectedValues.startDate),
        end: formatDate(selectedValues.endDate),
      },
    }));
    setIsCalendarModalOpen(false);
  }, [selectedValues, setIsCalendarModalOpen, setSelectingDetails]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={style.calendarModalWrapper}
    >
      <div className={style.calendarModalInner}>
        <div>
          <div className={style.headerTitle}>
            <div className={style.headerTitleText}>
              <T8y variant="h2" color="light" bold>
                Select Hire Period
              </T8y>
              <T8y variant="t3" className={style.minText}>
                {daysText}
              </T8y>
            </div>
            <div
              className={style.clearIconWrapper}
              onClick={() => setIsCalendarModalOpen(false)}
              data-testid="close_datepicker-btn"
            >
              <ClearIcon />
            </div>
          </div>
          <div className={style.headerInfo}>
            <div className={style.headerInfoFirstLine}>
              <div className={style.calendarModalTextQuantity}>
                <T8y variant="t1v1" color="newGray">
                  Duration
                </T8y>
                &nbsp;
                <T8y
                  variant="t1v1"
                  className={
                    hiringDaysCount
                      ? style.hiringDaysCountBlack
                      : style.hiringDaysCountGray
                  }
                  bold
                >
                  {hiringDaysCount}
                  &nbsp;{hiringDaysCount === 1 ? 'day' : 'days'}
                </T8y>
              </div>
              <T8y
                variant="t2"
                color="newGray"
                className={style.clearText}
                onClick={() => {
                  onDatesChanges({ startDate: null, endDate: null });
                  changeFocusedPickerDay('start');
                }}
              >
                Clear dates
              </T8y>
            </div>
            <div className={style.calendarModalDateItems}>
              <DatePickerDisclosureItem
                date={!!selectedValues.startDate && startDate}
                titleSlug="hireStart"
                isDatesHighlighted={isDatesHighlighted}
                isInCalendar
              />
              <DatePickerDisclosureItem
                date={!!selectedValues.endDate && endDate}
                titleSlug="hireEnd"
                isDatesHighlighted={isDatesHighlighted}
                isInCalendar
              />
            </div>
          </div>
        </div>
        {!!suggestions.length && (
          <div className={style.suggestionsWrapper}>
            <div className={style.suggestions} id="suggested-dates">
              {suggestions.map((el, index) => (
                <div
                  key={index}
                  onClick={() => {
                    onDatesChanges(el, true);
                  }}
                  className={style.suggestionWrapper}
                >
                  {el.formattedStr}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={style.mobileCalendarBody} ref={calendarWrapperRef}>
          <DatePickerCalendar
            calendarState={state}
            start={selectedValues.startDate}
            end={selectedValues.endDate}
            handleDateChange={onDatesChanges}
            minBookingDate={minBookingDate}
            minBookingDays={minBookingDays}
            maxBookingDate={maxBookingDate}
            edge={state?.focusedInput === 'startDate' ? 'start' : 'end'}
            blockedDatesList={BLOCKED_DATES_LIST}
            numberOfMonths={12}
          />
        </div>
        <div className={style.mobileCalendarFooter}>
          <Button
            onClick={handleChangeDates}
            disabled={!selectedValues.endDate}
            className={style.saveBtn}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MobileCalendarModal;
